// Данные «Моих задач» (030: T029 – T031; contracts/api.md, «Мои задачи»; FR-019 – FR-022, FR-042):
// поиск адреса /my?view=open|done&group=overdue|today|week|later|none, разделение строк на
// «Открытые» и «Выполненные», группы по сроку, замена строки после ответа в кэше списка.
import { myItemGroups } from '@kp/contracts';
import type { MyItemGroup, MyItemRow, MyItems } from '@kp/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { apiKey, apiQuery } from '../../api/client.ts';

export type MyView = 'open' | 'done';

export interface MySearch {
  /** Сегмент: «Открытые» (по умолчанию) или «Выполненные». */
  view?: MyView | undefined;
  /** Группа срока — фильтр из «Главной»: «Мои: просрочено 2» → /my?group=overdue. */
  group?: MyItemGroup | undefined;
}

/** Чужие значения отбрасываются: корневой маршрут пропускает параметры адреса как есть. */
export function parseMySearch(search: Record<string, unknown>): MySearch {
  const view = search.view === 'done' ? 'done' : undefined;
  const group = myItemGroups.find((value) => value === search.group);
  return { view, group };
}

/** Ключ списка: после ответа строка заменяется в нём (setQueryData). */
export const myItemsKey = apiKey('my.items');

/**
 * Список перечитывается при каждом открытии экрана: пометки «новый», «срок … → …» считает сервер
 * по прошлому просмотру. Фоновое перечитывание при возврате на вкладку — по умолчанию клиента.
 */
export function myItemsQuery() {
  return { ...apiQuery('my.items'), staleTime: 0 };
}

/** Выполненный и отменённый пункт — в «Выполненных» (FR-019: отменённые — с пометкой). */
export function isDoneRow(row: Pick<MyItemRow, 'state' | 'cancelled'>): boolean {
  return row.state === 'done' || row.state === 'cancelled' || row.cancelled;
}

/** Пункт в системе — uuid; строка протокола в режиме файла — file:<версия>:<строка> (Д6). */
export function isFileKey(itemId: string): boolean {
  return itemId.startsWith('file:');
}

/** Ответить по пункту можно: не только чтение и ещё не выполнен (отмена — тоже конец). */
export function canAnswer(row: MyItemRow): boolean {
  return row.readOnly === null && !isFileKey(row.itemId);
}

export interface RowGroup {
  group: MyItemGroup;
  rows: MyItemRow[];
}

/** Строки по группам срока в порядке «Просрочено» → «Без срока»; пустые группы не видны. */
export function groupRows(rows: readonly MyItemRow[]): RowGroup[] {
  return myItemGroups
    .map((group) => ({ group, rows: rows.filter((row) => row.group === group) }))
    .filter((entry) => entry.rows.length > 0);
}

/** Выполненные — новые сверху: последнее изменение первым. */
export function doneOrder(rows: readonly MyItemRow[]): MyItemRow[] {
  return [...rows].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
}

/**
 * Строка после ответа или «Отменить» — на место прежней; счётчики сегментов пересчитываются:
 * «Выполнено» переводит строку из «Открытых» в «Выполненные».
 */
export function withRow(data: MyItems, row: MyItemRow): MyItems {
  const before = data.rows.find((item) => item.itemId === row.itemId);
  if (!before) return data;
  const wasDone = isDoneRow(before);
  const nowDone = isDoneRow(row);
  const shift = wasDone === nowDone ? 0 : nowDone ? 1 : -1;
  return {
    ...data,
    rows: data.rows.map((item) => (item.itemId === row.itemId ? row : item)),
    counts: {
      open: Math.max(0, data.counts.open - shift),
      done: Math.max(0, data.counts.done + shift),
    },
  };
}

/** Записать строку в кэш списка и сбросить счётчик меню и историю пункта. */
export function applyRow(queryClient: QueryClient, row: MyItemRow): void {
  queryClient.setQueryData<MyItems>(myItemsKey, (data) => (data ? withRow(data, row) : data));
  void queryClient.invalidateQueries({ queryKey: apiKey('my.summary') });
  void queryClient.invalidateQueries({ queryKey: ['api', 'my.history'] });
}
